import { RefreshCw, Repeat } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { classifyRunReadFailure } from "@/api/previewInsights";
import { usePreviewRunTurnsQuery } from "@/api/previewReplay";
import { useCapability } from "@/auth/SessionContext";
import { formatBytes, formatDateTime } from "@/lib/format";
import {
  CONTENT_UNAVAILABLE_LABELS,
  FAILURE_CODE_LABELS,
  OMISSION_REASON_LABELS,
  RUN_POLL_INTERVAL_MS,
  type PreviewRunDispatch,
  type PreviewRunView,
} from "@/lib/previewInsights";
import {
  PREVIEW_REPLAY_MAX_TURNS,
  REPLAY_RULES,
  TURN_STATUS_LABELS,
  eligibleBoundaries,
  readTurnBoundaries,
  type ReplayBoundary,
  type TurnBoundary,
} from "@/lib/previewReplay";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { ReplayFromTurnDialog } from "@/ui/preview/ReplayFromTurnDialog";
import { LoadingState, StaleDataNotice } from "@/ui/states";

/**
 * Replay from a turn — the run page's boundary list (#1105, approved design
 * §11.1 "select an earlier preview run and a user-turn boundary").
 *
 * Every synthetic user turn the source run's trace records is one row, read
 * from compact `turn` events — a turn number, when it happened, the
 * producer's one-line label, its status and whether its input body is
 * recorded, withheld or unreadable. NO BODY IS FETCHED OR RENDERED: a compact
 * event carries none, and this panel asks for none. Each eligible row offers
 * one action, "Replay through turn N"; the header offers the whole script.
 *
 * BOTH PERMISSIONS, mirrored from the server's grant and never widened: the
 * boundaries are read only with `preview.inspect` (the run page itself is
 * denied without it), and every action is disabled with its reason without
 * `preview.create` — a replay creates a preview session (approved design
 * §6.1). ADR 0001: unauthorized controls are disabled, never hidden.
 *
 * ABSENCE IS STATED. A run whose content is unavailable — never captured,
 * expired, refused by the target, produced under a contract this build does
 * not read — lists no boundary and says which of those it is, with the one
 * read that can change the answer. A trace with declared gaps names the turn
 * numbers this view cannot show, because a replay through turn 4 replays
 * turns 1–4 whether or not the trace shows turn 3.
 */
export function ReplayFromTurnPanel({
  projectId,
  previewRunRef,
  view,
  dispatch,
  onRefreshRun,
  pollIntervalMs = RUN_POLL_INTERVAL_MS,
}: {
  projectId: string;
  previewRunRef: string;
  view: PreviewRunView;
  dispatch: PreviewRunDispatch;
  /** Re-reads the run envelope — the recovery when content is unavailable. */
  onRefreshRun: () => void;
  pollIntervalMs?: number;
}) {
  const inspect = useCapability("preview.inspect");
  const create = useCapability("preview.create");
  const contentAvailable = view.run.contentAvailable;
  const turns = usePreviewRunTurnsQuery(projectId, previewRunRef, {
    enabled: inspect.allowed && contentAvailable,
  });
  const [boundary, setBoundary] = useState<ReplayBoundary | null>(null);
  const headingId = useId();

  /**
   * The list follows the run: when the envelope's highest sequence moves,
   * the turn events are re-read — once per movement, not on every poll of
   * an unchanged run. The first reading is the query's own.
   */
  const lastSequence = view.summary?.lastSequence ?? view.run.lastSequence ?? null;
  const seenSequence = useRef<number | null>(null);
  const refetchTurns = turns.refetch;
  useEffect(() => {
    if (
      contentAvailable &&
      seenSequence.current !== null &&
      lastSequence !== null &&
      lastSequence !== seenSequence.current
    ) {
      void refetchTurns();
    }
    seenSequence.current = lastSequence;
  }, [lastSequence, contentAvailable, refetchTurns]);

  const reading =
    turns.data?.events === null || turns.data?.events === undefined
      ? null
      : readTurnBoundaries(turns.data.events);
  const eligible = reading === null ? [] : eligibleBoundaries(reading);
  const actionsAllowed = inspect.allowed && create.allowed;
  const actionReason = !inspect.allowed
    ? `${inspect.reason ?? ""} Replaying needs inspect access to the source run.`
    : !create.allowed
      ? `${create.reason ?? ""} A replay creates a new preview session, which needs the "preview.create" permission.`
      : null;
  const reasonId = `${headingId}-action-reason`;
  const wholeScriptReason = actionReason ?? (
    eligible.length === 0
      ? "No synthetic user turn is recorded for this run yet, so there is no script to replay."
      : null
  );

  return (
    <Card role="region" aria-labelledby={headingId}>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle id={headingId}>Replay from a turn</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void turns.refetch()}
            disabled={!inspect.allowed || !contentAvailable || turns.isFetching}
          >
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Re-read turns
          </Button>
          <Button
            size="sm"
            onClick={() => setBoundary({ kind: "whole_script" })}
            disabled={wholeScriptReason !== null}
            aria-describedby={wholeScriptReason === null ? undefined : reasonId}
          >
            <Repeat aria-hidden="true" className="h-4 w-4" />
            Replay the whole script
          </Button>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Start a fresh synthetic preview that replays this run&apos;s user
          inputs, in order, into a testing version you choose, and inspect the
          new run. {REPLAY_RULES.inputsOnly} {REPLAY_RULES.nothingRestored}
        </p>

        {wholeScriptReason === null ? null : (
          <p id={reasonId} className="text-xs text-muted-foreground">
            {wholeScriptReason}
          </p>
        )}

        {!inspect.allowed ? null : !contentAvailable ? (
          <UnavailableSource view={view} onRefresh={onRefreshRun} />
        ) : turns.isPending ? (
          <LoadingState label="Loading the run's turns" rows={3} />
        ) : turns.isError && turns.data === undefined ? (
          <TurnsReadFailure error={turns.error} onRetry={() => void turns.refetch()} />
        ) : turns.data === undefined ? null : (
          <>
            {turns.isError ? (
              <StaleDataNotice
                updatedAt={turns.dataUpdatedAt}
                onRetry={() => void turns.refetch()}
              />
            ) : null}
            {reading === null ? (
              <UnavailableSource view={view} onRefresh={onRefreshRun} />
            ) : reading.boundaries.length === 0 ? (
              <p className="rounded-md border border-border px-3 py-3 text-sm text-muted-foreground">
                No synthetic user turn has been recorded for this run yet. A
                boundary appears here once the trace records one; re-read the
                turns to check.
              </p>
            ) : (
              <>
                <TurnNotes
                  missingTurnNumbers={reading.missingTurnNumbers}
                  duplicateCount={reading.duplicateCount}
                  foreignCount={reading.foreignCount}
                  truncated={turns.data.truncated}
                />
                <ol
                  aria-label="Turn boundaries"
                  className="divide-y divide-border rounded-md border border-border"
                >
                  {reading.boundaries.map((row, index) => (
                    <TurnRow
                      key={row.eventRef ?? `${row.sequence ?? "s"}-${index}`}
                      row={row}
                      actionsAllowed={actionsAllowed}
                      reasonId={actionReason === null ? undefined : reasonId}
                      onReplay={(turnNumber) =>
                        setBoundary({ kind: "through_turn", turnNumber })
                      }
                    />
                  ))}
                </ol>
                {actionReason === null || wholeScriptReason !== null ? null : (
                  <p id={reasonId} className="text-xs text-muted-foreground">
                    {actionReason}
                  </p>
                )}
              </>
            )}
          </>
        )}
      </CardBody>

      {boundary === null ? null : (
        <ReplayFromTurnDialog
          projectId={projectId}
          sourcePreviewRunRef={previewRunRef}
          boundary={boundary}
          visibleTurnCount={reading?.boundaries.length ?? 0}
          sourceDispatch={dispatch}
          onClose={() => setBoundary(null)}
          onRefreshTurns={() => void turns.refetch()}
          pollIntervalMs={pollIntervalMs}
        />
      )}
    </Card>
  );
}

/**
 * Why no boundary can be listed, from the run's own header — each an arm the
 * run page already folds (`derivePreviewRunView`), stated here in terms of
 * what it means for a replay. A source whose content the target no longer
 * holds cannot be replayed from (approved design §11.1: a checkpoint "remains
 * usable only while the source trace content is retained").
 */
function UnavailableSource({
  view,
  onRefresh,
}: {
  view: PreviewRunView;
  onRefresh: () => void;
}) {
  const { run } = view;
  const reasonSentence =
    run.contentUnavailableReason === null || run.contentUnavailableReason === undefined
      ? null
      : `${capitalize(CONTENT_UNAVAILABLE_LABELS[run.contentUnavailableReason])}, so its turns cannot be listed and no boundary can be chosen.`;
  let sentence: string;
  if (view.kind === "unavailable" && view.reason.kind === "capture_failed") {
    sentence = `This run's trace capture failed${
      view.reason.failureCode ? `: ${FAILURE_CODE_LABELS[view.reason.failureCode]}` : ""
    }. Its turns cannot be listed, so no boundary can be chosen.`;
  } else if (view.kind === "unavailable" && view.reason.kind === "producer_unsupported") {
    sentence =
      "The Brain that served this run predates the trace contract, so it recorded no turns to replay from.";
  } else if (view.kind === "unavailable" && view.reason.kind === "trace_unavailable") {
    sentence =
      "The serving Brain reports this run's trace as unavailable, so its turns cannot be listed and no boundary can be chosen.";
  } else if (reasonSentence !== null) {
    sentence = reasonSentence;
  } else if (view.kind === "unavailable") {
    sentence =
      "This run's content could not be read consistently, so its turns cannot be listed and no boundary can be chosen.";
  } else {
    sentence =
      "This run has no readable content yet, so its turns cannot be listed and no boundary can be chosen.";
  }
  return (
    <div className="space-y-2 rounded-md border border-border px-3 py-3 text-sm text-foreground">
      <p>{sentence}</p>
      <p className="text-xs text-muted-foreground">
        If the preview target captures or restores the content, the turns
        appear here; a run whose content has expired or been removed on the
        target can no longer be replayed from. Nothing about the run is
        assumed in the meantime.
      </p>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw aria-hidden="true" className="h-4 w-4" />
        Check the run again
      </Button>
    </div>
  );
}

function capitalize(sentence: string): string {
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

function TurnsReadFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const failure = classifyRunReadFailure(error);
  const sentence =
    failure.kind === "not_found"
      ? "No turns are readable for this run at this address for your account in this environment, so no boundary can be chosen."
      : failure.kind === "denied"
        ? "Your account is not authorized to read this run's turns."
        : failure.kind === "upstream"
          ? failure.recovery === "retry"
            ? "The preview target took too long to list this run's turns; the same read may well succeed."
            : "The preview target could not list this run's turns; an identical read is refused until an operator acts."
          : failure.kind === "transport"
            ? "No response arrived for the turn read; check the connection and try again."
            : failure.kind === "unauthenticated"
              ? "Your sign-in has expired."
              : failure.kind === "not_implemented"
                ? "Reading a run's events is not built in this environment."
                : `The Editor Service answered the turn read with "${failure.code}".`;
  return (
    <div role="alert" className="space-y-2 rounded-md border border-border px-3 py-3 text-sm text-foreground">
      <p>{sentence}</p>
      {failure.kind === "denied" || failure.kind === "not_implemented" ? null : (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          Try again
        </Button>
      )}
    </div>
  );
}

function TurnNotes({
  missingTurnNumbers,
  duplicateCount,
  foreignCount,
  truncated,
}: {
  missingTurnNumbers: number[];
  duplicateCount: number;
  foreignCount: number;
  truncated: boolean;
}) {
  const notes: string[] = [];
  if (missingTurnNumbers.length > 0) {
    notes.push(
      `Turn${missingTurnNumbers.length === 1 ? "" : "s"} ${missingTurnNumbers.join(", ")} ${
        missingTurnNumbers.length === 1 ? "is" : "are"
      } not in this trace (a declared gap or lost capture). A replay through a later turn still replays every input the preview target holds up to it.`,
    );
  }
  if (duplicateCount > 0) {
    notes.push(
      `${duplicateCount} event${duplicateCount === 1 ? "" : "s"} repeated a turn number already listed and ${duplicateCount === 1 ? "is" : "are"} not shown twice.`,
    );
  }
  if (foreignCount > 0) {
    notes.push(
      `${foreignCount} event${foreignCount === 1 ? "" : "s"} of another family arrived on a turn-only page and ${foreignCount === 1 ? "was" : "were"} ignored.`,
    );
  }
  if (truncated) {
    notes.push(
      "The run holds more turn events than this view reads; only the first pages are listed. The preview target replays at most " +
        `${PREVIEW_REPLAY_MAX_TURNS} turns either way.`,
    );
  }
  if (notes.length === 0) {
    return null;
  }
  return (
    <ul className="list-disc space-y-1 ps-5 text-xs text-muted-foreground">
      {notes.map((note) => (
        <li key={note}>{note}</li>
      ))}
    </ul>
  );
}

function describeInput(row: TurnBoundary): string {
  switch (row.input.state) {
    case "recorded":
      return row.input.byteSize === null
        ? "Input recorded"
        : `Input recorded (${formatBytes(row.input.byteSize)})`;
    case "withheld":
      return `Input withheld${
        row.input.reason ? `: ${OMISSION_REASON_LABELS[row.input.reason]}` : ""
      }`;
    case "unreadable":
      return "Input not readable";
  }
}

function TurnRow({
  row,
  actionsAllowed,
  reasonId,
  onReplay,
}: {
  row: TurnBoundary;
  actionsAllowed: boolean;
  reasonId: string | undefined;
  onReplay: (turnNumber: number) => void;
}) {
  const noteId = useId();
  const ineligible = row.ineligibility;
  const ineligibleSentence =
    ineligible === null
      ? null
      : ineligible.reason === "unnumbered"
        ? "This turn event carries no turn number, so it cannot name a replay boundary."
        : `Turn ${row.turnNumber} is beyond the preview target's turn budget of ${ineligible.budget}, so a replay cannot stop there.`;
  const disabled = !actionsAllowed || ineligible !== null;
  const describedBy =
    [ineligibleSentence === null ? null : noteId, actionsAllowed ? null : reasonId]
      .filter((id): id is string => id !== null && id !== undefined)
      .join(" ") || undefined;

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
          <span>
            {row.turnNumber === null ? "Turn (not numbered)" : `Turn ${row.turnNumber}`}
          </span>
          {row.status === null ? null : (
            <Badge tone={row.status === "failed" || row.status === "unavailable" ? "destructive" : row.status === "attention" ? "warning" : "neutral"}>
              {TURN_STATUS_LABELS[row.status]}
            </Badge>
          )}
        </p>
        {row.label === null ? null : (
          // An inline island: a Hebrew label reads right-to-left inside a
          // row that keeps the page's alignment, exactly as a project name
          // does inside the page header's sentence.
          <p className="text-sm text-foreground">
            <span dir="auto">{row.label}</span>
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {row.occurredAt === null ? (
            "Time not reported"
          ) : (
            <time dateTime={row.occurredAt} dir="ltr">
              {formatDateTime(row.occurredAt)}
            </time>
          )}
          {" · "}
          {describeInput(row)}
          {row.sequence === null ? null : (
            <>
              {" · "}
              Event <span dir="ltr">{row.sequence}</span>
            </>
          )}
        </p>
        {ineligibleSentence === null ? null : (
          <p id={noteId} className="text-xs text-warning-foreground">
            {ineligibleSentence}
          </p>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        aria-describedby={describedBy}
        onClick={() => {
          if (row.turnNumber !== null) {
            onReplay(row.turnNumber);
          }
        }}
      >
        <Repeat aria-hidden="true" className="h-4 w-4" />
        {row.turnNumber === null ? "Replay through this turn" : `Replay through turn ${row.turnNumber}`}
      </Button>
    </li>
  );
}
